import React from 'react';
import { GeneratorOptions, ConfigPreset } from '../../types';

interface ConfigPresetsProps {
  onSelect: (preset: Partial<GeneratorOptions>) => void;
  onClose: () => void;
}

export function ConfigPresets({ onSelect, onClose }: ConfigPresetsProps) {
  const presets: ConfigPreset[] = [
    {
      id: 'claude-desktop-mixed',
      name: 'Claude Desktop (Mixed)',
      description: 'Optimized for Claude Desktop with mixed server types',
      template: {} as any,
      category: 'productivity',
      tags: ['claude', 'desktop', 'mixed'],
      isBuiltIn: true,
    },
    {
      id: 'claude-desktop-remote',
      name: 'Claude Desktop (Remote)',
      description: 'Remote-only servers for Claude Desktop',
      template: {} as any,
      category: 'productivity', 
      tags: ['claude', 'desktop', 'remote'],
      isBuiltIn: true,
    },
    {
      id: 'cursor-dedicated',
      name: 'Cursor Editor',
      description: 'Dedicated servers optimized for Cursor editor',
      template: {} as any,
      category: 'development',
      tags: ['cursor', 'editor', 'dedicated'],
      isBuiltIn: true,
    },
    {
      id: 'zed-remote',
      name: 'Zed Editor',
      description: 'Remote endpoints for Zed editor integration',
      template: {} as any,
      category: 'development',
      tags: ['zed', 'editor', 'remote'],
      isBuiltIn: true,
    },
    {
      id: 'development-setup',
      name: 'Development Setup',
      description: 'Local development with environment variables',
      template: {} as any,
      category: 'development',
      tags: ['development', 'local', 'env'],
      isBuiltIn: true,
    },
    {
      id: 'production-setup',
      name: 'Production Setup', 
      description: 'Production-ready remote configuration',
      template: {} as any,
      category: 'productivity',
      tags: ['production', 'remote', 'stable'],
      isBuiltIn: true,
    },
  ];

  const presetConfigurations: Record<string, Partial<GeneratorOptions>> = {
    'claude-desktop-mixed': {
      templateType: 'mixed',
      baseUrl: 'http://localhost:3010',
      includeEnv: true,
      outputFormat: 'json',
      clientType: 'claude-desktop',
    },
    'claude-desktop-remote': {
      templateType: 'remote',
      baseUrl: 'http://localhost:3010',
      includeEnv: true,
      outputFormat: 'json',
      clientType: 'claude-desktop',
    },
    'cursor-dedicated': {
      templateType: 'dedicated',
      includeEnv: false,
      outputFormat: 'json',
      clientType: 'cursor',
    },
    'zed-remote': {
      templateType: 'remote',
      baseUrl: 'http://localhost:3010',
      includeEnv: true,
      outputFormat: 'json',
      clientType: 'zed',
    },
    'development-setup': {
      templateType: 'mixed',
      baseUrl: 'http://localhost:3010',
      includeEnv: true,
      outputFormat: 'json',
      clientType: 'custom',
    },
    'production-setup': {
      templateType: 'remote',
      baseUrl: 'https://your-mcp-server.com',
      includeEnv: false,
      outputFormat: 'json',
      clientType: 'custom',
    },
  };

  const categoryColors = {
    productivity: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    development: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    communication: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    data: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    custom: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
  };

  const handlePresetSelect = (presetId: string) => {
    const config = presetConfigurations[presetId];
    if (config) {
      onSelect(config);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Configuration Presets
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Choose a preset to quickly configure your MCP setup
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Presets Grid */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {presets.map((preset) => (
              <div
                key={preset.id}
                onClick={() => handlePresetSelect(preset.id)}
                className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 cursor-pointer transition-colors group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {preset.name}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {preset.description}
                    </p>
                  </div>
                  {preset.isBuiltIn && (
                    <span className="text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 px-2 py-1 rounded">
                      Built-in
                    </span>
                  )}
                </div>

                {/* Category and Tags */}
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${categoryColors[preset.category]}`}>
                    {preset.category}
                  </span>
                  <div className="flex items-center space-x-1">
                    {preset.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                    {preset.tags.length > 3 && (
                      <span className="text-xs text-gray-400">
                        +{preset.tags.length - 3}
                      </span>
                    )}
                  </div>
                </div>

                {/* Configuration Preview */}
                <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  {presetConfigurations[preset.id] && (
                    <>
                      <div className="flex items-center justify-between">
                        <span>Template:</span>
                        <span className="font-mono">
                          {presetConfigurations[preset.id].templateType}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Format:</span>
                        <span className="font-mono">
                          {presetConfigurations[preset.id].outputFormat}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Client:</span>
                        <span className="font-mono">
                          {presetConfigurations[preset.id].clientType}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Click on a preset to apply its configuration
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}